import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  build_requirement_form,
  format_quantity_constraint,
  format_available_sub_entity,
  normalize_max_count_input,
  parse_response_data
} from '../ManageSubEntities/manage_sub_entity_helpers.js';
import {
  CREATE_CHILD_REQUIREMENT_URL,
  CREATE_ENTITY_CONFIG_URL,
  GET_CHILD_REQUIREMENTS_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
  REMOVE_CHILD_REQUIREMENT_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'SubEntityRequirements',
  mixins: [alert_mixin],
  data: () => ({
    hierarchy_name: 'Parent Entity',
    loading: false,
    load_error: '',
    creating_requirement: false,
    removing_requirement_id: '',
    assigned_children_loading: false,
    audit_trail: {
      date: ''
    },
    requirement_cards: [],
    assigned_children: [],
    selected_requirement_child_id: '',
    requirement_form: build_requirement_form()
  }),
  computed: {
    entity_type_id() {
      const route_query_id = Number(this.$route?.query?.entityTypeId || 0);
      return Number.isInteger(route_query_id) && route_query_id > 0 ? route_query_id : null;
    },
    entity_config_id() {
      const route_query_id = Number(this.$route?.query?.entityConfigId || 0);
      return Number.isInteger(route_query_id) && route_query_id > 0 ? route_query_id : null;
    },
    hero_metrics() {
      const total_requirements = this.requirement_cards.length;
      const total_child_entities = this.assigned_children.length;

      return [
        {
          label: 'Total Requirements',
          value: total_requirements,
          icon: 'bi-ui-checks-grid',
          tone: 'primary'
        },
        {
          label: `Total Child Entities For ${this.hierarchy_name}`,
          value: total_child_entities,
          icon: 'bi-diagram-3',
          tone: 'warning'
        }
      ];
    },
    assignable_children() {
      return (this.assigned_children || []).map((item, index) => ({
        ...format_available_sub_entity({
          id: item?.sub_entity_type_id || item?.id || item?.rule_id,
          entity_type_name: item?.child_entity_type_name || item?.name
        }, index),
        description: String(item?.child_entity_type_description || '').trim()
      })).filter((item) => item.id);
    }
  },
  watch: {
    '$route.query.entityTypeId'() {
      this.fetch_child_requirements();
      this.fetch_assigned_children();
    },
    '$route.query.entityConfigId'() {
      this.fetch_child_requirements();
    }
  },
  mounted() {
    this.initialize_requirement_modal();
    this.fetch_child_requirements();
    this.fetch_assigned_children();
  },
  beforeUnmount() {
    this.teardown_requirement_modal();
  },
  methods: {
    parse_response_data(payload) {
      return parse_response_data(payload);
    },
    initialize_requirement_modal() {
      this.requirement_modal_element = document.getElementById('subentity_requirement_modal');
      if (!this.requirement_modal_element) {
        return;
      }

      this.requirement_modal_element.addEventListener(
        'hidden.bs.modal',
        this.handle_requirement_modal_hidden
      );
    },
    teardown_requirement_modal() {
      if (!this.requirement_modal_element || !this.handle_requirement_modal_hidden) {
        return;
      }

      this.requirement_modal_element.removeEventListener(
        'hidden.bs.modal',
        this.handle_requirement_modal_hidden
      );
    },
    format_date(value) {
      if (!value) {
        return '';
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    get_requirement_icon(name) {
      const normalized_name = String(name || '').toLowerCase();
      const icon_rules = [
        {
          icon: 'bi-diagram-3',
          matches: ['beneficiar'].some((keyword) => normalized_name.includes(keyword))
        },
        {
          icon: 'bi-people',
          matches: ['trustee', 'owner', 'director'].some((keyword) => normalized_name.includes(keyword))
        },
        {
          icon: 'bi-building-add',
          matches: ['funder', 'founder', 'shareholder'].some((keyword) => normalized_name.includes(keyword))
        }
      ];
      const matched_rule = icon_rules.find((rule) => rule.matches);

      return matched_rule?.icon || 'bi-box-seam';
    },
    get_requirement_name(requirement, child_entity_type_name, index) {
      const requirement_name = String(
        requirement?.display_name ||
        child_entity_type_name ||
        `Requirement ${index + 1}`
      ).trim();

      return requirement_name;
    },
    get_requirement_count_meta(min_count, max_count) {
      const normalized_max_count = normalize_max_count_input(max_count);
      const has_max_count = normalized_max_count !== null && Number.isInteger(normalized_max_count);
      const count_meta = {
        quantity_constraint: format_quantity_constraint({ min_count, max_count }),
        max_label: has_max_count ? normalized_max_count : 'Unlimited',
        quantity_mode: has_max_count ? 'Fixed' : 'Flexible'
      };

      return count_meta;
    },
    get_requirement_onboarding_meta(onboarding_mode) {
      const onboarding_meta = onboarding_mode === 'self_service'
        ? {
            onboarding_icon: 'bi-lightning-charge',
            onboarding_label: 'Self-Service'
          }
        : {
            onboarding_icon: 'bi-person-gear',
            onboarding_label: 'Manual Processing'
          };

      return onboarding_meta;
    },
    format_requirement_card(requirement, index) {
      const min_count = Number(requirement?.min_count || 0);
      const max_count = requirement?.max_count;
      const onboarding_mode = String(requirement?.onboarding_mode || '').trim();
      const child_entity_type_name = String(requirement?.child_entity_type_name || '').trim();
      const name = this.get_requirement_name(requirement, child_entity_type_name, index);
      const description = child_entity_type_name || 'Linked child entity requirement';
      const icon = this.get_requirement_icon(requirement?.display_name || child_entity_type_name);
      const count_meta = this.get_requirement_count_meta(min_count, max_count);
      const onboarding_meta = this.get_requirement_onboarding_meta(onboarding_mode);

      const requirement_card = {
        id: requirement?.id || `requirement-${index}`,
        name,
        description,
        icon,
        min_count,
        quantity_constraint: count_meta.quantity_constraint,
        max_label: count_meta.max_label,
        quantity_mode: count_meta.quantity_mode,
        onboarding_icon: onboarding_meta.onboarding_icon,
        onboarding_label: onboarding_meta.onboarding_label,
        is_required: Boolean(requirement?.is_required_for_completion),
        display_order: Number(requirement?.display_order || 0),
        is_active: requirement?.is_active !== false,
        date_created: this.format_date(requirement?.date_created),
        binding_id: Number(requirement?.id || 0),
        raw_requirement: requirement
      };

      return requirement_card;
    },
    build_requirement_payload(entity_config_id) {
      const sub_entity_type_id = Number(this.selected_requirement_child_id);
      const min_count = Number(this.requirement_form.min_count);
      const max_count = normalize_max_count_input(this.requirement_form.max_count);
      const display_order = Number(this.requirement_form.display_order);
      const display_name = String(this.requirement_form.display_name || '').trim();
      const onboarding_mode = String(this.requirement_form.onboarding_mode || '').trim();

      if (!Number.isInteger(entity_config_id) || entity_config_id <= 0) {
        this.show_error('Unable to determine the current entity configuration.');
        return null;
      }

      if (!Number.isInteger(sub_entity_type_id) || sub_entity_type_id <= 0) {
        this.show_error('Please select a child entity.');
        return null;
      }

      if (!display_name) {
        this.show_error('Please enter a display name for this requirement.');
        return null;
      }

      if (!Number.isInteger(min_count) || min_count < 0) {
        this.show_error('Minimum count must be a valid number greater than or equal to 0.');
        return null;
      }

      if (max_count !== null && (!Number.isInteger(max_count) || max_count < min_count)) {
        this.show_error('Maximum count must be blank for no maximum or a valid number greater than or equal to minimum count.');
        return null;
      }

      if (!onboarding_mode) {
        this.show_error('Please select an onboarding mode.');
        return null;
      }

      if (!Number.isInteger(display_order) || display_order < 0) {
        this.show_error('Display order must be a valid number greater than or equal to 0.');
        return null;
      }

      const payload = {
        entity_config_id,
        sub_entity_type_id,
        display_name,
        min_count,
        max_count,
        onboarding_mode,
        is_required_for_completion: Boolean(this.requirement_form.is_required_for_completion),
        display_order
      };

      return payload;
    },
    build_remove_requirement_payload(requirement) {
      const binding_id = Number(requirement?.binding_id || requirement?.id || 0);

      if (!Number.isInteger(binding_id) || binding_id <= 0) {
        this.show_error('Unable to determine the selected child requirement.');
        return null;
      }

      const remove_requirement_payload = { binding_id };

      return remove_requirement_payload;
    },
    async fetch_full_config() {
      const response = await axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id: this.entity_type_id
          }
        })
      );

      return this.parse_response_data(response.data).data || {};
    },
    async ensure_entity_config_exists() {
      if (this.entity_config_id) {
        return this.entity_config_id;
      }

      if (!this.entity_type_id) {
        return;
      }

      const config_data = await this.fetch_full_config().catch(async () => {

        await axios.post(
          CREATE_ENTITY_CONFIG_URL,
          {
            entity_type_id: this.entity_type_id
          },
          get_request_config()
        );

        return this.fetch_full_config();
      });

      const resolved_entity_config_id = Number(config_data?.entity_config?.id);
      if (!Number.isInteger(resolved_entity_config_id) || resolved_entity_config_id <= 0) {
        return;
      }

      return resolved_entity_config_id;
    },
    async fetch_assigned_children() {
      if (!this.entity_type_id) {
        this.assigned_children = [];
        return;
      }

      this.assigned_children_loading = true;

      try {
        const response = await axios.get(
          LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
          get_request_config({
            params: {
              parent_entity_type_id: this.entity_type_id
            }
          })
        );
        const response_data = this.parse_response_data(response.data);
        this.assigned_children = Array.isArray(response_data.data) ? response_data.data : [];
      } catch {
        this.assigned_children = [];
      } finally {
        this.assigned_children_loading = false;
      }
    },
    async fetch_child_requirements() {
      this.loading = true;
      this.load_error = '';
      this.requirement_cards = [];

      try {
        const resolved_entity_config_id = await this.ensure_entity_config_exists();
        const response = await axios.get(
          GET_CHILD_REQUIREMENTS_URL,
          get_request_config({
            params: {
              entity_config_id: resolved_entity_config_id
            }
          })
        );
        const response_data = this.parse_response_data(response.data);
        const requirements = Array.isArray(response_data.data) ? response_data.data : [];

        this.hierarchy_name = String(requirements[0]?.parent_entity_type_name || this.hierarchy_name).trim() || 'Parent Entity';
        this.audit_trail.date = this.format_date(
          requirements.reduce((latest, item) => {
            const current_value = String(item?.date_created || '').trim();
            if (!current_value) {
              return latest;
            }

            if (!latest) {
              return current_value;
            }

            return new Date(current_value) > new Date(latest) ? current_value : latest;
          }, '')
        );
        this.requirement_cards = requirements
          .filter((item) => item?.is_active !== false)
          .sort((left, right) => Number(left?.display_order || 0) - Number(right?.display_order || 0))
          .map((item, index) => this.format_requirement_card(item, index));
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.requirement_cards = [];
        this.load_error = response_data.message || 'Failed to load child requirements.';
      } finally {
        this.loading = false;
      }
    },
    add_requirement() {
      const first_child = this.assignable_children[0] || null;
      this.selected_requirement_child_id = first_child?.id || '';
      this.requirement_form = build_requirement_form(first_child?.name || '', this.requirement_cards.length);
    },
    on_requirement_child_change() {
      const selected_child = this.assignable_children.find(
        (item) => String(item.id) === String(this.selected_requirement_child_id)
      );
      const preserved_form = {
        ...build_requirement_form(selected_child?.name || '', this.requirement_form.display_order),
        min_count: this.requirement_form.min_count,
        max_count: this.requirement_form.max_count,
        onboarding_mode: this.requirement_form.onboarding_mode,
        is_required_for_completion: this.requirement_form.is_required_for_completion,
        display_order: this.requirement_form.display_order
      };

      this.requirement_form = preserved_form;
    },
    handle_requirement_modal_hidden() {
      this.close_requirement_modal();
    },
    close_requirement_modal() {
      this.selected_requirement_child_id = '';
      this.requirement_form = build_requirement_form();
    },
    async delete_requirement(requirement) {
      const payload = this.build_remove_requirement_payload(requirement);
      if (!payload) {
        return;
      }

      this.removing_requirement_id = String(requirement?.id || '');

      try {
        const response = await axios.post(
          REMOVE_CHILD_REQUIREMENT_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        this.requirement_cards = this.requirement_cards.filter(
          (item) => String(item.id) !== String(requirement?.id)
        );
        this.show_success(response_data.message || 'Child requirement removed');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to remove child requirement.');
      } finally {
        this.removing_requirement_id = '';
      }
    },
    remove_requirement(requirement) {
      this.show_confirm(
        'Remove Child Requirement',
        `Are you sure you want to remove ${requirement?.name || 'this child requirement'}?`,
        () => {
          this.delete_requirement(requirement);
        }
      );
    },
    async create_requirement() {
      let entity_config_id = null;

      try {
        entity_config_id = await this.ensure_entity_config_exists();
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to prepare entity configuration for child requirements.');
        return;
      }

      const payload = this.build_requirement_payload(entity_config_id);
      if (!payload) {
        return;
      }

      this.creating_requirement = true;

      try {
        const response = await axios.post(
          CREATE_CHILD_REQUIREMENT_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        await this.fetch_child_requirements();
        this.show_success(response_data.message || 'Child requirement created successfully.');
        this.close_modal('subentity_requirement_modal');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to create child requirement.');
      } finally {
        this.creating_requirement = false;
      }
    }
  }
};
